import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {current} from '@reduxjs/toolkit';
import {
  APIResponse,
  ConsultationPaymentRequestPayload,
  ConsultationPaymentSuccessResponse,
  FileItem,
  GetConsultationSpecialistListRequestPayload,
  GetConsultationSpecialistListSuccessResponse,
  GetSpecialistListRequestPayload,
  GetSpecialistListSuccessResponse,
  PatientDashboardSliceInitalState,
  SelectConsultationSpecialistRequestPayload,
  SelectConsultationSpecialistSuccessResponse,
  GetPendingConsultationCaseSuccessResponse,
  ConsultationIdPayload,
  GetConsultationListRequestPayload,
  GetConsultationListSuccessResponse,
  UploadAdditionalFilesSuccessResponse,
  SubmitFeedbackAndRatingRequestPayload,
  SubmitFeedbackAndRatingSuccessResponse,
  CancelConsultationSuccessResponse,
  GetSpecialistDetailsRequestPayload,
  GetSpecialistDetailsSuccessResponse,
  GetSpecialistFeedbackRequestPayload,
  GetSpecialistFeedbackSuccessResponse,
  Doctor,
  Consultation,
  AnalyzeHealthReportRequestPayload,
  AnalyzeHealthReportSuccessResponse,
  AnalyzeHealthReportErrorResponse,
  ProceedWithAiRecommendedSpecialistRequestPayload,
  ProceedWithAiRecommendedSpecialistSuccessResponse,
  AssignHospitalSpecialistRequestPayload,
  AssignHospitalSpecialistSuccessResponse,
  GetHospitalDetailsRequestPayload,
  GetHospitalDetailsSuccessResponse,
  GetHospitalSpecialistsListRequestPayload,
  GetHospitalSpecialistsListSuccessResponse,
  ManualCategorySelectionRequestPayload,
  ManualCategorySelectionSuccessResponse,
  RefferalConsultationRequestPayload,
  FileSerializableItem,
  ConsultationIdSuccessResponse,
  AddConsultationReportSuccessResponse,
  RemoveConsultationReportPayload,
  RemoveConsultationReportSuccessResponse,
  RemoveAdditionalConsultationReportPayload,
  RemoveAdditionalConsultationReportSuccessResponse,
  AddConsultationQueryRequestPayload,
  AddConsultationQuerySuccessResponse,
  SelectSpecialistAndProceedWithPaymentRequestPayload,
  SelectSpecialistAndProceedWithPaymentSuccessResponse,
  SpecialistBookingType,
} from '@/interface';
import {
  ConsulationFlowType,
  ConsultationGroup,
  ConsultationStatus,
  FAILURE_KEY,
  ProgressBarSteps,
  ReferralType,
  SpecialistEntityType,
  SUCCESS_KEY,
} from '@/constants';
import {isEmpty} from 'ramda';
import CryptoJS from 'crypto-js';
import {ActionButtons} from '@/components';

const initialState: PatientDashboardSliceInitalState = {
  isLoading: false,
  analyzeReportLoading: false, // new state specialist for analyzing document loading
  uploadAdditionalFilesLoading: false,
  selectSpecialistLoading: false,
  uploadFilesLoading: {},

  patientDashboardSuccess: false,
  patientDashboardFailure: false,
  dashboardState: {
    expected_category: null, // for error handling
    linked_to: null,
    linked_category: null,
    step: 1,
    recommended_specialists: [],
    consultation_type: null,
    category: null,
    original_category: null,
    status: 0,
    specialist: {
      id: null,
      name: '',
    },
    hospital_name: null,
    payment: {
      amount: 0,
      status: false,
      url: '',
      paymentTimer: '',
    },
    files: [],
    consultation_id: null,
    rejected_files: null,
    flow: null,
    is_manual_flag: false,
    previous_files: [],
    hospital_specialization: [],
    existing_file_ids: [],
    specialist_recent_files: [],
  },
  new_consultation: false,
  specialistList: {
    specialists: [],
    currentPage: 1,
    nextPage: null,
    totalPages: 1,
  },
  consultations: {
    current_tab: ConsultationGroup.Ongoing,
    consultation: [],
    currentPage: 0,
    nextPage: null,
    totalPages: 0,
    totalResults: 0,
    uploadAdditionalFilesMeta: {},
    cancelConsultationLoading: {}, // Track cancel loading per consultation ID
    rejectedFiles: null,
  },
  specialistDetails: {
    isLoading: false,
    data: null,
  },
  specialistFeedbacks: {
    isLoading: false,
    feedbacks: [],
    currentPage: 1,
    nextPage: null,
    totalPages: 1,
  },
  hospitalDetails: {
    isLoading: false,
    data: null,
  },

  hospitalSpecialistsList: {
    isLoading: false,
    data: [],
  },

  hospitalBookingModalState: null,

  // Recent cases for showing "Last Consulted" on specialist cards
  recentCases: [],
};

const patientDashboardSlice = createSlice({
  name: 'patientDashboard',
  initialState,
  reducers: {
    setExistingFileIds: (state, action: PayloadAction<number[]>) => {
      state.dashboardState.existing_file_ids = action.payload;
    },
    analyzeHealthReportRequest: (state, action: PayloadAction<AnalyzeHealthReportRequestPayload>) => {
      state.analyzeReportLoading = ProgressBarSteps.NotStarted;
      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;

      // Clear rejected_files when starting a new upload
      state.dashboardState.rejected_files = [];

      // state reset
      state.dashboardState.step = 1;
      if (state.dashboardState.flow === ConsulationFlowType.Regular) {
        state.dashboardState.category = null;
      }
      state.dashboardState.recommended_specialists = [];
      state.dashboardState.specialist = {
        id: null,
        name: '',
      };
      state.dashboardState.payment = {
        status: false,
        amount: 0,
      };
    },
    analyzeHealthReportSuccess: (state, action: PayloadAction<AnalyzeHealthReportSuccessResponse>) => {
      state.patientDashboardSuccess = action.payload.status_code;

      if (action.payload.status_code === 'S-10020') {
        state.dashboardState.category = action.payload.data.category;
        state.dashboardState.consultation_id = action.payload.data.id;
        state.dashboardState.recommended_specialists = action.payload.data.specialist;

        // Skip step 2 (Select Specialist) for hospital assigned flow - go directly to step 3 (Payment)
        if (state.dashboardState.flow === ConsulationFlowType.HospitalSpecialistAssigned) {
          if (action.payload.data.amount) {
            state.dashboardState.payment.amount = action.payload.data.amount;
          }
          if (action.payload.data.hospital_id) {
            state.dashboardState.specialist.id = action.payload.data.hospital_id;
          }
          if (action.payload.data.hospital_name) {
            state.dashboardState.specialist.name = action.payload.data.hospital_name;
          }

          state.analyzeReportLoading = ProgressBarSteps.CaseAnalysisStarted;
        }

        // reset expected_category
        state.dashboardState.expected_category = null;
      }
      if (action.payload.data.hospital_specializations) {
        state.dashboardState.hospital_specialization = action.payload.data.hospital_specializations;
      }
    },
    analyzeHealthReportFailure: (state, action: PayloadAction<AnalyzeHealthReportErrorResponse>) => {
      state.analyzeReportLoading = false;
      if (!action.payload.cancelled) {
        state.patientDashboardFailure = action.payload.status_code;
      }

      // Clear rejected_files on failure to prevent showing stale data
      state.dashboardState.rejected_files = null;

      // Store rejected_files if present
      if (action.payload.data?.rejected_files && action.payload.data.rejected_files.length > 0) {
        state.dashboardState.rejected_files = action.payload.data.rejected_files;
      }

      if (action.payload.status_code === 'E-10097' && action.payload.data?.expected_category) {
        state.dashboardState.expected_category = action.payload.data?.expected_category || null;
      }
    },
    cancelAnalyzeHealthReportRequest: state => {
      // Reset loading state when cancelling
      state.analyzeReportLoading = false;
    },

    // =================================
    // proceed with ai recommended specialist
    // =================================
    proceedWithAiRecommendedSpecialistRequest: (state, action: PayloadAction<ProceedWithAiRecommendedSpecialistRequestPayload>) => {
      state.isLoading = true;
      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;
    },
    proceedWithAiRecommendedSpecialistSuccess: (state, action: PayloadAction<ProceedWithAiRecommendedSpecialistSuccessResponse>) => {
      state.isLoading = false;
      state.patientDashboardSuccess = action.payload.status_code;
      if (action.payload.status_code === 'S-10101') {
        state.dashboardState.category = action.payload.data.category;
        state.dashboardState.recommended_specialists = action.payload.data.specialist;
        state.dashboardState.step = 2;
        state.dashboardState.flow = ConsulationFlowType.Regular;
      }
    },
    proceedWithAiRecommendedSpecialistFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.patientDashboardFailure = action.payload.status_code;
    },

    /**
     * select specialist
     */
    selectSpecialistRequest: (state, action: PayloadAction<SelectConsultationSpecialistRequestPayload>) => {
      state.selectSpecialistLoading = true;
      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;
    },

    selectSpecialistSuccess: (state, action: PayloadAction<SelectConsultationSpecialistSuccessResponse>) => {
      state.selectSpecialistLoading = false;
      state.patientDashboardSuccess = action.payload.status_code;
      if (action.payload.status_code === 'S-10027') {
        state.dashboardState.step = 3;
        state.dashboardState.payment.amount = action.payload.data.amount;
        state.dashboardState.consultation_id = action.payload.data.id;
        state.dashboardState.specialist = {
          id: action.payload.data.specialist_id,
          name: action.payload.data.specialist_name,
        };
        // reset payment status
        state.dashboardState.payment.status = false;
      }
    },
    selectSpecialistFailure: (state, action: PayloadAction<APIResponse>) => {
      state.selectSpecialistLoading = false;
      state.patientDashboardFailure = action.payload.status_code;
    },

    getSpecialistListRequest: (state, action: PayloadAction<GetConsultationSpecialistListRequestPayload>) => {
      state.isLoading = true;
      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;
      state.dashboardState.category = action.payload.specialization_id;
    },
    getSpecialistListSuccess: (state, action: PayloadAction<GetConsultationSpecialistListSuccessResponse>) => {
      state.isLoading = false;
      state.dashboardState.recommended_specialists = action.payload.data.recommended_specialists;
      state.dashboardState.is_manual_flag = action.payload.data.is_manual_category_flag;
      state.dashboardState.consultation_id = action.payload.data.id;
      state.dashboardState.category = action.payload.data.category;

      if (state.dashboardState.is_manual_flag) {
        if (state.dashboardState.flow === ConsulationFlowType.PreSelectedSpecialist) {
          state.dashboardState.flow = ConsulationFlowType.Regular;
        }
        // reset the specialist if changing the category after manual category selection
        state.dashboardState.specialist = {
          id: null,
          name: '',
        };
      }
    },
    getSpecialistListFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.patientDashboardFailure = action.payload.status_code;
    },

    consultationPaymentRequest: (state, action: PayloadAction<ConsultationPaymentRequestPayload>) => {
      state.isLoading = true;
      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;
    },
    consultationPaymentSuccess: (state, action: PayloadAction<ConsultationPaymentSuccessResponse>) => {
      state.isLoading = false;
      state.patientDashboardSuccess = action.payload.status_code;
      if (action.payload.data) {
        state.dashboardState.payment.url = action.payload.data.payment_url;
      }
    },
    consultationPaymentFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.patientDashboardFailure = action.payload.status_code;
    },

    getAllSpecialistsRequest: (state, action: PayloadAction<GetSpecialistListRequestPayload>) => {
      state.isLoading = true;
      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;
    },
    getAllSpecialistsSuccess: (state, action: PayloadAction<GetSpecialistListSuccessResponse>) => {
      state.isLoading = false;
      state.patientDashboardSuccess = action.payload.status_code;

      // Pagination: replace on page 1, append on subsequent pages
      if (action.payload.data.current_page === 1) {
        state.specialistList.specialists = action.payload.data.specialists;
      } else {
        state.specialistList.specialists = [...state.specialistList.specialists, ...action.payload.data.specialists];
      }

      state.specialistList.currentPage = action.payload.data.current_page;
      state.specialistList.nextPage = action.payload.data.next_page;
      state.specialistList.totalPages = action.payload.data.total_pages;
    },
    getAllSpecialistsFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.patientDashboardFailure = action.payload.status_code;
      if (action.payload.status_code === 'E-10076') {
        state.specialistList.specialists = [];
        state.specialistList.currentPage = 0;
        state.specialistList.nextPage = null;
        state.specialistList.totalPages = 0;
      }
    },

    /**
     * new consultation check
     */
    newConsultationCheckRequest: state => {
      state.isLoading = true;
      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;
    },
    newConsultationCheckSuccess: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      if (action.payload.status_code === 'S-10033') {
        state.new_consultation = true;
      }
    },
    newConsultationCheckFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      if (action.payload.status_code === 'E-10090') {
        state.new_consultation = false;
      }
    },

    /*
     * get pending consultation case
     */
    getPendingConsultationCaseRequest: state => {
      state.isLoading = true;
      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;
    },
    getPendingConsultationCaseSuccess: (state, action: PayloadAction<GetPendingConsultationCaseSuccessResponse>) => {
      state.isLoading = false;
      state.dashboardState.consultation_id = action.payload.data.id;
      state.dashboardState.files = action.payload.data.files;
      state.dashboardState.category = action.payload.data.category;
      state.dashboardState.original_category = action.payload.data.original_category;
      state.dashboardState.step = action.payload.data.step;
      state.dashboardState.consultation_type = action.payload.data.type;
      state.dashboardState.flow = action.payload.data.flow;
      state.dashboardState.is_manual_flag = action.payload.data.is_manual_flag;
      state.dashboardState.patient_query = action.payload.data.patient_query;
      // optionally update these
      if (action.payload.data.recommended_specialists) {
        state.dashboardState.recommended_specialists = action.payload.data.recommended_specialists;
      }
      if (action.payload.data.specialist) {
        state.dashboardState.specialist = action.payload.data.specialist;
      }
      if (action.payload.data.payment) {
        state.dashboardState.payment = action.payload.data.payment;
      }
      if (action.payload.data.linked_to && !isEmpty(action.payload.data.linked_to)) {
        state.dashboardState.linked_to = action.payload.data.linked_to;

        if (action.payload.data.category) {
          state.dashboardState.linked_category = action.payload.data.category;
        }
      }
      if (action.payload.data.hospital_name) {
        state.dashboardState.hospital_name = action.payload.data.hospital_name;
      }
      state.dashboardState.previous_files = action.payload.data?.referred_documents;
      if (action.payload.data.hospital_specializations) {
        state.dashboardState.hospital_specialization = action.payload.data.hospital_specializations;
      }
      if (action.payload.data.expire_at) {
        state.dashboardState.expire_at = action.payload.data.expire_at;
      }
      state.dashboardState.specialist_recent_files = action.payload.data.specialist_recent_files || [];
    },

    getPendingConsultationCaseFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      if (action.payload.status_code === 'E-10093') {
        state.dashboardState = initialState.dashboardState;
      }
    },

    getConsultationListRequest: (state, action: PayloadAction<GetConsultationListRequestPayload>) => {
      state.isLoading = true;
      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;
    },
    getConsultationListSuccess: (state, action: PayloadAction<GetConsultationListSuccessResponse>) => {
      state.isLoading = false;

      if (action.payload.data.current_page === 1) {
        state.consultations.consultation = action.payload.data.consultations;
      } else {
        state.consultations.consultation = [...state.consultations.consultation, ...action.payload.data.consultations];
      }
      state.consultations.currentPage = action.payload.data.current_page;
      state.consultations.nextPage = action.payload.data.next_page;
      state.consultations.totalPages = action.payload.data.total_pages;
    },
    getConsultationListFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      if (action.payload.status_code === 'E-10091') {
        state.consultations.consultation = [];
      }
    },

    uploadAdditionalConsultationFilesRequest: (state, action: PayloadAction<any>) => {
      // since we are passing form data
      const id = action.payload.get('id') as number;
      const files = action.payload.getAll('files') as Array<File>;

      // temporarily add the dummy files for skeleton animation loading
      const loadingMap: Record<string, FileSerializableItem> = {};
      // add files with index
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const uuid = CryptoJS.lib.WordArray.random(4).toString();
        loadingMap[uuid] = {
          id: i,
          name: file.name,
          size: file.size,
          url: URL.createObjectURL(file),
        };
      }

      // temporarily add the dummy files for skeleton animation loading
      state.consultations.uploadAdditionalFilesMeta[id.toString()] = loadingMap;

      state.uploadAdditionalFilesLoading = ProgressBarSteps.CaseAnalysisStarted;
      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;

      // Clear rejected_files when starting a new upload to prevent showing stale data
      state.dashboardState.rejected_files = null;
    },
    uploadAdditionalConsultationFilesSuccess: (state, action: PayloadAction<UploadAdditionalFilesSuccessResponse>) => {
      state.uploadAdditionalFilesLoading = false;
      state.patientDashboardSuccess = action.payload.status_code;

      const consultationId = action.payload.data.id;

      state.consultations.consultation = state.consultations.consultation.map(consultation => {
        if (consultation.id === consultationId) {
          consultation.additional_files = [...consultation.additional_files, ...action.payload.data.files];
        }
        return consultation;
      });

      // reset expected_category
      state.dashboardState.expected_category = null;

      // Store rejected_files if present in the success response
      if (action.payload.data.rejected_files && action.payload.data.rejected_files.length > 0) {
        state.consultations.rejectedFiles = action.payload.data.rejected_files;
      }

      // remove the dummy files from the upload additional files meta
      delete state.consultations.uploadAdditionalFilesMeta[consultationId.toString()];
    },
    cancelUploadAdditionalFilesRequest: (state, action: PayloadAction<{id?: number} | undefined>) => {
      // Reset loading state when cancelling
      state.uploadAdditionalFilesLoading = false;

      // Reset upload file meta if consultation ID is provided
      if (action.payload?.id) {
        delete state.consultations.uploadAdditionalFilesMeta[action.payload.id.toString()];
      } else {
        // If no ID provided, clear all upload meta
        state.consultations.uploadAdditionalFilesMeta = {};
      }
    },
    uploadAdditionalConsultationFilesFailure: (
      state,
      action: PayloadAction<
        {data: {id: number; expected_category?: number | null; rejected_files?: Array<{filename: string; status_code: string; message: string}>}} & APIResponse
      >,
    ) => {
      state.uploadAdditionalFilesLoading = false;
      if (!action.payload.cancelled) {
        state.patientDashboardFailure = action.payload.status_code;
      }

      // remove the dummy files from the upload additional files meta
      delete state.consultations.uploadAdditionalFilesMeta[action.payload.data.id.toString()];

      if (action.payload.status_code === 'E-10097' && action.payload.data?.expected_category) {
        state.dashboardState.expected_category = action.payload.data?.expected_category || null;
      }

      // Store rejected_files if present in the failure response (for invalid files)
      if (action.payload.data?.rejected_files && action.payload.data.rejected_files.length > 0) {
        state.dashboardState.rejected_files = action.payload.data.rejected_files;
      } else {
        // Clear rejected_files if not present in response to prevent showing stale data
        state.dashboardState.rejected_files = null;
      }
    },

    /**
     * remove additional consultation document
     */
    removeAdditionalConsultationFileRequest: (state, action: PayloadAction<RemoveAdditionalConsultationReportPayload>) => {
      const fileId = action.payload.file_id;
      const consultationId = action.payload.id;

      const meta = state.consultations.uploadAdditionalFilesMeta[consultationId] ?? {};
      meta[fileId] = true;

      state.consultations.uploadAdditionalFilesMeta[consultationId] = meta;

      state.uploadAdditionalFilesLoading = true;
      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;
    },
    removeAdditionalConsultationFileSuccess: (state, action: PayloadAction<RemoveAdditionalConsultationReportSuccessResponse>) => {
      state.uploadAdditionalFilesLoading = false;
      if (action.payload.status_code) state.patientDashboardSuccess = action.payload.status_code;
      const consultationId = action.payload.data.id;

      state.consultations.consultation = state.consultations.consultation.map(c => {
        if (c.id === consultationId) {
          const previousFile = current(c.additional_files) ?? [];
          const removedFile = action.payload.data.file_id;
          // const updatedFiles = action.payload.data.files;
          // const removedFiles = previousFile.filter(prevFile => !updatedFiles.some(updatedFile => updatedFile.id === prevFile.id));

          const meta = state.consultations.uploadAdditionalFilesMeta[consultationId] ?? {};
          if (meta && removedFile) {
            delete meta[removedFile];
          }
          if (isEmpty(meta)) {
            delete state.consultations.uploadAdditionalFilesMeta[consultationId];
          } else {
            state.consultations.uploadAdditionalFilesMeta[consultationId] = meta;
          }
          c.additional_files = previousFile.filter(prevFile => prevFile.id !== removedFile);
        }
        return c;
      });
    },
    removeAdditionalConsultationFileFailure: (state, action: PayloadAction<APIResponse & {id: number}>) => {
      state.uploadAdditionalFilesLoading = false;
      state.patientDashboardFailure = action.payload.status_code;

      delete state.consultations.uploadAdditionalFilesMeta[action.payload.id.toString()];
    },

    /**
     * add consultation report
     */
    addConsultationReportRequest: (state, action: PayloadAction<any>) => {
      const files = action.payload.getAll('files') as Array<File>;

      // temporarily add the dummy files for skeleton animation loading
      const loadingMap: Record<string, FileSerializableItem> = {};
      // add files with index
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const uuid = CryptoJS.lib.WordArray.random(4).toString();
        loadingMap[uuid] = {
          id: i,
          name: file.name,
          size: file.size,
          url: URL.createObjectURL(file),
        };
      }

      state.uploadFilesLoading = {
        ...state.uploadFilesLoading,
        ...loadingMap,
      };
      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;
    },

    addConsultationReportSuccess: (state, action: PayloadAction<AddConsultationReportSuccessResponse>) => {
      state.patientDashboardSuccess = action.payload.status_code;
      state.dashboardState.consultation_id = action.payload.data.id;

      const updatedFiles = action.payload.data.files;

      const updatedSet = new Set(updatedFiles.map(f => `${f.name}-${f.size}`));

      state.uploadFilesLoading = Object.fromEntries(
        Object.entries(state.uploadFilesLoading).filter(([_, value]) => {
          if (typeof value === 'boolean') return false;

          const key = `${value.name}-${value.size}`;
          return !updatedSet.has(key);
        }),
      );

      state.dashboardState.files = [...state.dashboardState.files, ...updatedFiles];

      // reset the other state (only for online second opinion)
      if (state.dashboardState.flow === ConsulationFlowType.Regular) {
        state.dashboardState.category = null;
      }
      state.dashboardState.recommended_specialists = [];
      state.dashboardState.step = 1;
      state.dashboardState.specialist = {
        id: null,
        name: '',
      };
      state.dashboardState.payment = {
        status: false,
        amount: 0,
      };
    },
    addConsultationReportFailure: (state, action: PayloadAction<APIResponse>) => {
      state.uploadFilesLoading = {};
      state.patientDashboardFailure = action.payload.status_code;
      state.dashboardState.files = [];
    },
    /**
     * remove consultation report
     */
    removeConsultationReportRequest: (state, action: PayloadAction<RemoveConsultationReportPayload>) => {
      const fileId = action.payload.id;
      state.uploadFilesLoading[fileId] = true;

      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;
    },
    removeConsultationReportSuccess: (state, action: PayloadAction<RemoveConsultationReportSuccessResponse>) => {
      state.patientDashboardSuccess = action.payload.status_code;
      const previousFile = state.dashboardState.files || [];
      const file_id = action.payload.data.file_id;

      delete state.uploadFilesLoading[file_id];

      state.dashboardState.files = previousFile.filter(prevFile => prevFile.id !== file_id);

      if (state.dashboardState.flow === ConsulationFlowType.Regular) {
        state.dashboardState.category = null;
      }
      state.dashboardState.step = 1;
      state.dashboardState.recommended_specialists = [];
      state.dashboardState.specialist = {
        id: null,
        name: '',
      };
      state.dashboardState.payment = {
        status: false,
        amount: 0,
      };

      state.dashboardState.is_manual_flag = false;
    },
    removeConsultationReportFailure: (state, action: PayloadAction<APIResponse & {id: string}>) => {
      delete state.uploadFilesLoading[action.payload.id];
      state.patientDashboardFailure = action.payload.status_code;
    },

    /**
     * submit feedback and rating
     */
    submitFeedbackAndRatingRequest: (state, action: PayloadAction<SubmitFeedbackAndRatingRequestPayload>) => {
      state.isLoading = true;
      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;
    },
    submitFeedbackAndRatingSuccess: (state, action: PayloadAction<SubmitFeedbackAndRatingSuccessResponse>) => {
      state.isLoading = false;
      state.patientDashboardSuccess = action.payload.status_code;
      state.consultations.consultation = state.consultations.consultation.map(consultation => {
        if (consultation.id === action.payload.data.id) {
          consultation.feedback_submitted = true;
        }
        return consultation;
      });
    },
    submitFeedbackAndRatingFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.patientDashboardFailure = action.payload.status_code;
    },

    /**
     * cancel consultation
     */
    cancelCancelConsultationRequest: (state, action: PayloadAction<ConsultationIdPayload>) => {
      const consultationId = action.payload.id;
      // Clear loading state for this consultation (cancel the in-flight request UX)
      if (state.consultations.cancelConsultationLoading) {
        delete state.consultations.cancelConsultationLoading[consultationId.toString()];
      }
      // Don't set success/failure for a user-cancelled request
    },
    cancelConsultationRequest: (state, action: PayloadAction<ConsultationIdPayload>) => {
      const consultationId = action.payload.id;
      // Initialize cancelConsultationLoading if it doesn't exist
      if (!state.consultations.cancelConsultationLoading) {
        state.consultations.cancelConsultationLoading = {};
      }
      // Set loading state for this specific consultation
      state.consultations.cancelConsultationLoading[consultationId.toString()] = true;
      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;
    },
    cancelConsultationSuccess: (state, action: PayloadAction<CancelConsultationSuccessResponse>) => {
      const consultationId = action.payload.data.id;
      // Clear loading state for this consultation
      if (state.consultations.cancelConsultationLoading) {
        delete state.consultations.cancelConsultationLoading[consultationId.toString()];
      }
      state.patientDashboardSuccess = action.payload.status_code;
      // Remove the cancelled consultation from the list
      state.consultations.consultation = state.consultations.consultation.filter(consultation => {
        return consultation.id !== consultationId;
      });
      // Also clear upload meta for this consultation if it exists
      if (state.consultations.uploadAdditionalFilesMeta[consultationId.toString()]) {
        delete state.consultations.uploadAdditionalFilesMeta[consultationId.toString()];
      }
    },
    cancelConsultationFailure: (state, action: PayloadAction<APIResponse & {cancelled?: boolean; data?: {id?: number}}>) => {
      // Clear loading state - try to get ID from action payload or response data
      const consultationId = action.payload?.data?.id;
      if (consultationId && state.consultations.cancelConsultationLoading) {
        delete state.consultations.cancelConsultationLoading[consultationId.toString()];
      }
      // Don't show an error for user-cancelled requests
      if (!action.payload.cancelled) {
        state.patientDashboardFailure = action.payload.status_code;
      }
    },

    /**
     * follow up consultation
     */
    followUpConsultationRequest: (state, action: PayloadAction<ConsultationIdPayload>) => {
      state.isLoading = true;
      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;
    },
    followUpConsultationSuccess: (state, action: PayloadAction<ConsultationIdSuccessResponse>) => {
      state.isLoading = false;
      state.patientDashboardSuccess = action.payload.status_code;
      state.dashboardState.consultation_id = action.payload.data.id;

      // reset so that the patient can start a new consultation
      state.dashboardState.step = 1;
    },
    followUpConsultationFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.patientDashboardFailure = action.payload.status_code;
    },

    /**
     * referral consultation
     */
    referralConsultationRequest: (state, action: PayloadAction<RefferalConsultationRequestPayload>) => {
      state.isLoading = true;
      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;
    },
    referralConsultationSuccess: (state, action: PayloadAction<ConsultationIdSuccessResponse>) => {
      state.isLoading = false;
      state.patientDashboardSuccess = action.payload.status_code;
      state.dashboardState.consultation_id = action.payload.data.id;

      // reset so that the patient can start a new consultation
      state.dashboardState.step = 1;
    },
    referralConsultationFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.patientDashboardFailure = action.payload.status_code;
    },

    // manual category selection
    manualCategorySelectionRequest: (state, action: PayloadAction<ManualCategorySelectionRequestPayload>) => {
      state.isLoading = true;
      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;
    },
    manualCategorySelectionSuccess: (state, action: PayloadAction<ManualCategorySelectionSuccessResponse>) => {
      state.isLoading = false;
      state.patientDashboardSuccess = action.payload.status_code;
      state.dashboardState.is_manual_flag = action.payload.data.is_manual_category_flag;

      // if the flow is pre-selected specialist, then set the flow to regular
      if (state.dashboardState.flow === ConsulationFlowType.PreSelectedSpecialist) {
        state.dashboardState.flow = ConsulationFlowType.Regular;
      }
      if (!action.payload.data.is_manual_category_flag) {
        state.dashboardState.category = action.payload.data.original_category;
        state.dashboardState.original_category = action.payload.data.original_category;
      }
    },
    manualCategorySelectionFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.patientDashboardFailure = action.payload.status_code;
    },

    // ====================================
    // specialist booking api
    // ====================================
    specialistBookingRequest: (state, action: PayloadAction<SpecialistBookingType>) => {
      state.isLoading = true;
      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;
    },
    specialistBookingSuccess: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.patientDashboardSuccess = action.payload.status_code;

      // reset so that the patient can start a new consultation
      state.dashboardState.step = 1;
    },
    specialistBookingFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.patientDashboardFailure = action.payload.status_code;
    },

    // ====================================
    // assign hospital specialist api
    // ====================================
    assignHospitalSpecialistRequest: (state, action: PayloadAction<AssignHospitalSpecialistRequestPayload>) => {
      state.isLoading = true;
      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;
    },
    assignHospitalSpecialistSuccess: (state, action: PayloadAction<AssignHospitalSpecialistSuccessResponse>) => {
      state.isLoading = false;
      state.patientDashboardSuccess = action.payload.status_code;

      state.dashboardState.consultation_id = action.payload.data.id;

      // reset so that the patient can start a new consultation
      state.dashboardState.step = 1;
    },
    assignHospitalSpecialistFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.patientDashboardFailure = action.payload.status_code;
    },
    // ====================================
    // get hospital details api
    // ====================================
    getHospitalDetailsRequest: (state, action: PayloadAction<GetHospitalDetailsRequestPayload>) => {
      if (!state.hospitalDetails) {
        state.hospitalDetails = {
          isLoading: false,
          data: null,
        };
      }
      state.hospitalDetails.isLoading = true;
      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;
    },
    getHospitalDetailsSuccess: (state, action: PayloadAction<GetHospitalDetailsSuccessResponse>) => {
      if (!state.hospitalDetails) {
        state.hospitalDetails = {
          isLoading: false,
          data: null,
        };
      }
      state.hospitalDetails.isLoading = false;
      state.patientDashboardSuccess = action.payload.status_code;
      state.hospitalDetails.data = action.payload.data;
    },
    getHospitalDetailsFailure: (state, action: PayloadAction<APIResponse>) => {
      if (!state.hospitalDetails) {
        state.hospitalDetails = {
          isLoading: false,
          data: null,
        };
      }
      state.hospitalDetails.isLoading = false;
      state.patientDashboardFailure = action.payload.status_code;
    },

    // ====================================
    // get hospital specialists list api
    // ====================================
    getHospitalSpecialistsListRequest: (state, action: PayloadAction<GetHospitalSpecialistsListRequestPayload>) => {
      if (!state.hospitalSpecialistsList) {
        state.hospitalSpecialistsList = {
          isLoading: false,
          data: [],
        };
      }
      state.isLoading = true;
      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;
    },
    getHospitalSpecialistsListSuccess: (state, action: PayloadAction<GetHospitalSpecialistsListSuccessResponse>) => {
      if (!state.hospitalSpecialistsList) {
        state.hospitalSpecialistsList = {
          isLoading: false,
          data: [],
        };
      }
      state.isLoading = false;
      state.patientDashboardSuccess = action.payload.status_code;
      state.hospitalSpecialistsList.data = action.payload.data.specialists;
    },
    getHospitalSpecialistsListFailure: (state, action: PayloadAction<APIResponse>) => {
      if (!state.hospitalSpecialistsList) {
        state.hospitalSpecialistsList = {
          isLoading: false,
          data: [],
        };
      }
      state.isLoading = false;
      state.patientDashboardFailure = action.payload.status_code;
    },

    // ====================================
    // select hospital specialist api
    // ====================================
    selectHospitalSpecialistRequest: (state, action: PayloadAction<ConsultationIdPayload>) => {
      state.isLoading = true;
      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;
    },
    selectHospitalSpecialistSuccess: (state, action: PayloadAction<AssignHospitalSpecialistSuccessResponse>) => {
      state.isLoading = false;
      state.patientDashboardSuccess = action.payload.status_code;
      state.dashboardState.consultation_id = action.payload.data.id;

      // reset so that the patient can start a new consultation
      state.dashboardState.step = 1;
    },
    selectHospitalSpecialistFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.patientDashboardFailure = action.payload.status_code;
    },

    // ==========================================
    // select specialist and proceed with payment
    // ==========================================
    selectSpecialistAndProceedWithPaymentRequest(state, action: PayloadAction<SelectConsultationSpecialistRequestPayload>) {
      state.selectSpecialistLoading = true;
      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;
    },
    selectSpecialistAndProceedWithPaymentSuccess(state, action: PayloadAction<SelectSpecialistAndProceedWithPaymentSuccessResponse>) {
      state.selectSpecialistLoading = false;
      state.patientDashboardSuccess = action.payload.status_code;
      state.dashboardState.step = 4;
      state.dashboardState.specialist = {
        id: action.payload.data.specialist_id,
        name: action.payload.data.specialist_name,
      };
      // reset payment status
      state.dashboardState.payment.status = false;
    },
    selectSpecialistAndProceedWithPaymentFailure(state, action: PayloadAction<APIResponse>) {
      state.selectSpecialistLoading = false;
      state.patientDashboardFailure = action.payload.status_code;
    },

    /**
     * utlity
     */
    resetPatientDashboardMessage: state => {
      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;
      state.consultations.rejectedFiles = null;
      state.dashboardState.rejected_files = null;
    },
    clearE10080Data: state => {
      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;
      state.dashboardState.recommended_specialists = [];
    },
    resetPaymentUrl: state => {
      state.dashboardState.payment.url = '';
    },

    setPatientDashboardMessage: (state, action: PayloadAction<{status: 'success' | 'error'; statusCode: string}>) => {
      const {status, statusCode} = action.payload;
      if (status === 'success') {
        if (statusCode === 'S-10028') {
          state.dashboardState.payment.status = SUCCESS_KEY;
          state.patientDashboardSuccess = statusCode;
          state.dashboardState.payment.paymentTimer = new Date().toISOString();
        }
      } else {
        if (statusCode === 'E-10072') {
          state.dashboardState.payment.status = FAILURE_KEY;
        }
        state.patientDashboardFailure = statusCode;
      }
    },

    updateProgressBarStep: (state, action: PayloadAction<ProgressBarSteps>) => {
      state.analyzeReportLoading = action.payload;
    },

    updateUploadAdditionalFilesProgressBarStep: (state, action: PayloadAction<ProgressBarSteps>) => {
      state.uploadAdditionalFilesLoading = action.payload;
    },

    moveToStep4: state => {
      if (state.dashboardState.payment.status === SUCCESS_KEY) {
        state.dashboardState.step = 4;
      }
    },

    resetPaymentStatus: state => {
      state.dashboardState.payment.status = false;
    },

    resetPatientDashboardState: state => {
      state.dashboardState.step = 1;
      state.dashboardState.category = null;
      state.dashboardState.payment.url = '';
      state.dashboardState.payment.amount = 0;
      state.dashboardState.consultation_id = null;
      state.dashboardState.specialist.name = '';
      state.dashboardState.payment.status = false;
      state.dashboardState.recommended_specialists = [];
      state.dashboardState.files = [];
      state.dashboardState.rejected_files = [];
      state.dashboardState.expected_category = null;
      state.dashboardState.linked_to = null;
      state.dashboardState.linked_category = null;
    },

    setFiles: (state, action: PayloadAction<FileItem[]>) => {
      state.dashboardState.files = action.payload;
    },

    resetSpecialistList: state => {
      state.specialistList.specialists = [];
      state.specialistList.currentPage = 1;
      state.specialistList.nextPage = null;
      state.specialistList.totalPages = 1;
    },

    setCurrentConsultationTab: (state, action: PayloadAction<ConsultationGroup>) => {
      state.consultations.current_tab = action.payload;
    },

    switchSpecialistFromPatientDashboardStep1: state => {
      state.dashboardState.payment = {
        amount: 0,
        status: false,
      };
      state.dashboardState.step = 2;
    },

    cancelPatientDashboardRequest(state) {
      state.isLoading = false;
      state.analyzeReportLoading = false;
      state.uploadAdditionalFilesLoading = false;
      state.selectSpecialistLoading = false;

      state.uploadFilesLoading = {};

      // reset the upload additional files meta
      state.consultations.uploadAdditionalFilesMeta = {};

      // reset the cancel consultation loading
      state.consultations.cancelConsultationLoading = {};
    },

    removeRejectedFiles(state) {
      const rejected = state.dashboardState.rejected_files;
      if (!rejected || rejected.length === 0) return;
      // state.dashboardState.files = state.dashboardState.files.filter(file => !rejected.some(r => r.filename === file.name));

      // reset the state
      state.dashboardState.rejected_files = null;
    },

    /**
     * get specialist details api
     */
    getSpecialistDetailsRequest: (state, action: PayloadAction<GetSpecialistDetailsRequestPayload>) => {
      // Initialize specialistDetails if it doesn't exist (for backward compatibility with persisted state)
      if (!state.specialistDetails) {
        state.specialistDetails = {
          isLoading: false,
          data: null,
        };
      }
      state.specialistDetails.isLoading = true;
      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;
    },
    getSpecialistDetailsSuccess: (state, action: PayloadAction<GetSpecialistDetailsSuccessResponse & {specialistId?: number}>) => {
      // Initialize specialistDetails if it doesn't exist (for backward compatibility with persisted state)
      if (!state.specialistDetails) {
        state.specialistDetails = {
          isLoading: false,
          data: null,
        };
      }
      state.specialistDetails.isLoading = false;
      state.patientDashboardSuccess = action.payload.status_code;

      try {
        // Transform API response to Doctor interface
        const apiData = action.payload.data;
        const specialistId = (action.payload as any).specialistId; // Get id from payload if available

        if (!apiData) {
          state.specialistDetails.data = null;
          return;
        }

        // Helper to extract location string
        let locationString = '';
        if (apiData.location) {
          if (typeof apiData.location === 'string') {
            locationString = apiData.location;
          } else if (typeof apiData.location === 'object' && apiData.location.label) {
            locationString = apiData.location.label;
          }
        }

        // Ensure specialization is always an object with id and label
        let specialization = {id: 0, label: ''};
        if (apiData.specialization) {
          if (typeof apiData.specialization === 'object' && apiData.specialization !== null) {
            specialization = {
              id: apiData.specialization.id || 0,
              label: apiData.specialization.label || '',
            };
          }
        }

        const doctor: any & {is_hospital_specialist: boolean} = {
          id: specialistId || 0, // Use specialist id from request
          name: apiData.name || '',
          specialization: specialization,
          designation: apiData.designation || '',
          education: apiData.education || '',
          location: apiData?.location ? {id: apiData.location?.id || 0, label: apiData.location?.label || ''} : null,
          price: apiData.price || 0,
          rating: apiData.rating || 0,
          experience: apiData.years_of_experience || 0,
          image: (apiData.photo as any) || '', // Use photo from API response
          is_hospital_specialist: false,
          entity_type: SpecialistEntityType.Specialist,
          hospital_name: apiData.hospital_name || '',
          hospital_address: apiData.hospital_address || '',
        };

        // Add additional fields that might be needed
        (doctor as any).professional_experience = apiData.professional_experience || '';
        (doctor as any).qualification = apiData.qualification || '';
        (doctor as any).awards_and_achievements = apiData.awards_and_achievements || [];
        (doctor as any).areas_of_expertise = apiData.areas_of_expertise || [];
        (doctor as any).is_hospital_specialist = apiData.is_hospital_specialist || false;

        state.specialistDetails.data = doctor;
      } catch (error) {
        state.specialistDetails.data = null;
      }
    },
    getSpecialistDetailsFailure: (state, action: PayloadAction<APIResponse>) => {
      // Initialize specialistDetails if it doesn't exist (for backward compatibility with persisted state)
      if (!state.specialistDetails) {
        state.specialistDetails = {
          isLoading: false,
          data: null,
        };
      }
      state.specialistDetails.isLoading = false;
      state.patientDashboardFailure = action.payload.status_code;
      state.specialistDetails.data = null;
    },

    /**
     * get specialist feedbacks api
     */
    getSpecialistFeedbacksRequest: (state, action: PayloadAction<GetSpecialistFeedbackRequestPayload>) => {
      // Initialize specialistFeedbacks if it doesn't exist
      if (!state.specialistFeedbacks) {
        state.specialistFeedbacks = {
          isLoading: false,
          feedbacks: [],
          currentPage: 1,
          nextPage: null,
          totalPages: 1,
        };
      }
      state.specialistFeedbacks.isLoading = true;
      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;
    },
    getSpecialistFeedbacksSuccess: (state, action: PayloadAction<GetSpecialistFeedbackSuccessResponse>) => {
      // Initialize specialistFeedbacks if it doesn't exist
      if (!state.specialistFeedbacks) {
        state.specialistFeedbacks = {
          isLoading: false,
          feedbacks: [],
          currentPage: 1,
          nextPage: null,
          totalPages: 1,
        };
      }
      state.specialistFeedbacks.isLoading = false;
      state.patientDashboardSuccess = action.payload.status_code;

      if (action.payload.data.current_page === 1) {
        state.specialistFeedbacks.feedbacks = action.payload.data.feedback;
      } else {
        state.specialistFeedbacks.feedbacks = [...state.specialistFeedbacks.feedbacks, ...action.payload.data.feedback];
      }
      state.specialistFeedbacks.currentPage = action.payload.data.current_page;
      state.specialistFeedbacks.nextPage = action.payload.data.next_page;
      state.specialistFeedbacks.totalPages = action.payload.data.total_pages;
    },
    getSpecialistFeedbacksFailure: (state, action: PayloadAction<APIResponse>) => {
      // Initialize specialistFeedbacks if it doesn't exist
      if (!state.specialistFeedbacks) {
        state.specialistFeedbacks = {
          isLoading: false,
          feedbacks: [],
          currentPage: 1,
          nextPage: null,
          totalPages: 1,
        };
      }
      state.specialistFeedbacks.isLoading = false;
      state.patientDashboardFailure = action.payload.status_code;
    },

    moveToNextStepOnProgressBarComplete: state => {
      if (state.dashboardState.flow === ConsulationFlowType.HospitalSpecialistAssigned) {
        state.dashboardState.step = 3;
      } else {
        state.dashboardState.step = 2;
      }
      state.analyzeReportLoading = false;
    },

    // for real time updates
    updateConsultationData(state, action: PayloadAction<{id: number; data: Partial<Omit<Consultation, 'id'>>}>) {
      state.consultations.consultation = state.consultations.consultation
        .map(consultation => {
          // update the consultation in the same tab
          if (consultation.id === action.payload.id) {
            // on ongoing tab, if the status is report ready, then remove the consultation from the list
            if (action.payload.data?.status?.id === ConsultationStatus.ReportReady && state.consultations.current_tab === ConsultationGroup.Ongoing) {
              return null;
            }
            return {...consultation, ...action.payload.data};
          }
          return consultation;
        })
        .filter(consultation => consultation !== null);
    },

    activateOrDeactivateSpecialist: (state, action: PayloadAction<{id: number; is_active: boolean}>) => {
      state.specialistList.specialists = state.specialistList.specialists.map(specialist => {
        if (specialist.id === action.payload.id) {
          return {...specialist, is_active: action.payload.is_active};
        }
        return specialist;
      });

      state.dashboardState.recommended_specialists = state.dashboardState.recommended_specialists.map(specialist => {
        if (specialist.id === action.payload.id) {
          return {...specialist, is_active: action.payload.is_active};
        }
        return specialist;
      });
    },

    activateOrDeactivateHospitalSpecialist: (state, action: PayloadAction<{hospital_id: number; is_active: boolean}>) => {
      state.specialistList.specialists = state.specialistList.specialists.map(specialist => {
        if (specialist.hospital_id === action.payload.hospital_id) {
          return {...specialist, is_active: action.payload.is_active};
        }
        return specialist;
      });

      state.dashboardState.recommended_specialists = state.dashboardState.recommended_specialists.map(specialist => {
        if (specialist.hospital_id === action.payload.hospital_id) {
          return {...specialist, is_active: action.payload.is_active};
        }
        return specialist;
      });

      state.recentCases = state.recentCases.map(specialist => {
        if (specialist.hospital_id === action.payload.hospital_id) {
          return {...specialist, is_active: action.payload.is_active};
        }
        return specialist;
      });
    },

    updateSpecialistData(state, action: PayloadAction<{id: number; feeCurrency: number; feeAmount?: number}>) {
      state.specialistList.specialists = state.specialistList.specialists.map(specialist => {
        if (specialist.id === action.payload.id) {
          return {...specialist, fee_currency: action.payload.feeCurrency, charge: action.payload.feeAmount, price: action.payload.feeAmount ?? 0};
        }
        return specialist;
      });

      state.dashboardState.recommended_specialists = state.dashboardState.recommended_specialists.map(specialist => {
        if (specialist.id === action.payload.id) {
          return {...specialist, fee_currency: action.payload.feeCurrency, charge: action.payload.feeAmount, price: action.payload.feeAmount ?? 0};
        }
        return specialist;
      });

      if (state.hospitalSpecialistsList) {
        state.hospitalSpecialistsList.data = state.hospitalSpecialistsList.data.map(specialist => {
          if (specialist.id === action.payload.id) {
            return {...specialist, fee_currency: action.payload.feeCurrency, charge: action.payload.feeAmount, price: action.payload.feeAmount ?? 0};
          }
          return specialist;
        });
      }

      if (state.dashboardState.specialist.id === action.payload.id && state.dashboardState.step === 3 && state.dashboardState.payment.status === false) {
        state.dashboardState.payment.amount = action.payload.feeAmount ?? 0;
      }

      if (state.specialistDetails && state.specialistDetails.data && state.specialistDetails.data.id === action.payload.id) {
        state.specialistDetails.data = {...state.specialistDetails.data, price: action.payload.feeAmount ?? 0};
      }
    },

    // ====================================
    // add consultation query api
    // ====================================
    addConsultationQueryRequest: (state, action: PayloadAction<AddConsultationQueryRequestPayload>) => {
      state.isLoading = true;
      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;
    },
    addConsultationQuerySuccess: (state, action: PayloadAction<AddConsultationQuerySuccessResponse>) => {
      state.isLoading = false;
      state.patientDashboardSuccess = action.payload.status_code;
      state.dashboardState.patient_query = action.payload.data.query;
      if (action.payload.data.id != null) {
        state.dashboardState.consultation_id = action.payload.data.id;
      }
    },
    addConsultationQueryFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.patientDashboardFailure = action.payload.status_code;
    },

    // ====================================
    // hospital booking modal persistence
    // ====================================
    setHospitalBookingModalState: (
      state,
      action: PayloadAction<{
        returnPath: string;
        hospitalId: number;
        hospitalName: string;
        specializations: Array<{id: number; label: string}>;
        selectedSpecialization?: number | null;
        isStep2?: boolean;
        dashboardSearch: string;
      }>,
    ) => {
      const p = action.payload;
      state.hospitalBookingModalState = {
        returnPath: p.returnPath,
        hospitalId: p.hospitalId,
        hospitalName: p.hospitalName,
        specializations: p.specializations,
        selectedSpecialization: p.selectedSpecialization ?? null,
        isStep2: p.isStep2 ?? false,
        dashboardSearch: p.dashboardSearch,
      };
    },
    updateHospitalBookingModalState: (state, action: PayloadAction<{selectedSpecialization?: number | null; isStep2?: boolean}>) => {
      if (state.hospitalBookingModalState) {
        const p = action.payload;
        if (p.selectedSpecialization !== undefined) state.hospitalBookingModalState.selectedSpecialization = p.selectedSpecialization;
        if (p.isStep2 !== undefined) state.hospitalBookingModalState.isStep2 = p.isStep2;
      }
    },
    clearHospitalBookingModalState: state => {
      state.hospitalBookingModalState = null;
    },

    // ==================== Update Favourite Status (for WebSocket events) ====================
    updateFavouriteStatus: (state, action: PayloadAction<{specialist_id: number; is_favorite: boolean}>) => {
      const specialistIndex = state.specialistList.specialists.findIndex((s: Doctor) => s.id === action.payload.specialist_id);
      if (specialistIndex !== -1) {
        state.specialistList.specialists[specialistIndex].is_favourite = action.payload.is_favorite;
      }
    },

    // ==================== Remove Favourite Doctor ====================
    removeFavouriteDoctorRequest: (state, action: PayloadAction<{specialist_id: number}>) => {
      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;
    },
    removeFavouriteDoctorSuccess: (state, action: PayloadAction<APIResponse & {data: {specialist_id: number}}>) => {
      state.patientDashboardSuccess = action.payload.status_code;
      // Update is_favorite in recommended_specialists (dashboard view) - PRIMARY
      const dashboardIndex = state.dashboardState.recommended_specialists.findIndex((s: Doctor) => s.id === action.payload.data.specialist_id);
      if (dashboardIndex !== -1) {
        state.dashboardState.recommended_specialists[dashboardIndex].is_favourite = false;
      }
      // Also update in specialistList.specialists (specialist list view) - SECONDARY
      const specialistIndex = state.specialistList.specialists.findIndex((s: Doctor) => s.id === action.payload.data.specialist_id);
      if (specialistIndex !== -1) {
        state.specialistList.specialists[specialistIndex].is_favourite = false;
      }
    },
    removeFavouriteDoctorFailure: (state, action: PayloadAction<APIResponse>) => {
      state.patientDashboardFailure = action.payload.status_code;
    },

    // ==================== Add Favourite Doctor ====================
    addFavouriteDoctorRequest: (state, action: PayloadAction<{specialist_id: number}>) => {
      state.patientDashboardFailure = false;
      state.patientDashboardSuccess = false;
    },
    addFavouriteDoctorSuccess: (state, action: PayloadAction<APIResponse & {data: {specialist_id: number}}>) => {
      state.patientDashboardSuccess = action.payload.status_code;
      // Update is_favorite in recommended_specialists (dashboard view) - PRIMARY
      const dashboardIndex = state.dashboardState.recommended_specialists.findIndex((s: Doctor) => s.id === action.payload.data.specialist_id);
      if (dashboardIndex !== -1) {
        state.dashboardState.recommended_specialists[dashboardIndex].is_favourite = true;
      }
      // Also update in specialistList.specialists (specialist list view) - SECONDARY
      const specialistIndex = state.specialistList.specialists.findIndex((s: Doctor) => s.id === action.payload.data.specialist_id);
      if (specialistIndex !== -1) {
        state.specialistList.specialists[specialistIndex].is_favourite = true;
      }
    },
    addFavouriteDoctorFailure: (state, action: PayloadAction<APIResponse>) => {
      state.patientDashboardFailure = action.payload.status_code;
    },

    // ==================== Get Recent Cases ====================
    getRecentCasesRequest: state => {
      state.patientDashboardFailure = false;
    },
    getRecentCasesSuccess: (state, action: PayloadAction<APIResponse & {data: Array<any>}>) => {
      state.recentCases = action.payload.data;
    },
    getRecentCasesFailure: (state, action: PayloadAction<APIResponse>) => {
      state.patientDashboardFailure = action.payload.status_code;
    },
  },
});

export const {
  // upload heal report api
  analyzeHealthReportRequest,
  cancelAnalyzeHealthReportRequest,
  analyzeHealthReportSuccess,
  analyzeHealthReportFailure,

  // proceed with ai recommended specialist
  proceedWithAiRecommendedSpecialistRequest,
  proceedWithAiRecommendedSpecialistSuccess,
  proceedWithAiRecommendedSpecialistFailure,

  // select specialist api
  selectSpecialistRequest,
  selectSpecialistSuccess,
  selectSpecialistFailure,

  // get specialist list
  getSpecialistListRequest,
  getSpecialistListSuccess,
  getSpecialistListFailure,

  // payment apis
  consultationPaymentRequest,
  consultationPaymentSuccess,
  consultationPaymentFailure,

  // get all specialist for specialization screen
  getAllSpecialistsRequest,
  getAllSpecialistsSuccess,
  getAllSpecialistsFailure,

  // awating response api
  newConsultationCheckRequest,
  newConsultationCheckSuccess,
  newConsultationCheckFailure,

  // get pending case
  getPendingConsultationCaseFailure,
  getPendingConsultationCaseRequest,
  getPendingConsultationCaseSuccess,

  // get consultation list
  getConsultationListRequest,
  getConsultationListSuccess,
  getConsultationListFailure,

  // upload additional documents api
  uploadAdditionalConsultationFilesRequest,
  cancelUploadAdditionalFilesRequest,
  uploadAdditionalConsultationFilesSuccess,
  uploadAdditionalConsultationFilesFailure,

  // add medical report
  addConsultationReportRequest,
  addConsultationReportSuccess,
  addConsultationReportFailure,

  // remove medical report
  removeConsultationReportRequest,
  removeConsultationReportSuccess,
  removeConsultationReportFailure,

  // remove additional document
  removeAdditionalConsultationFileRequest,
  removeAdditionalConsultationFileSuccess,
  removeAdditionalConsultationFileFailure,

  // submit feedback and rating
  submitFeedbackAndRatingRequest,
  submitFeedbackAndRatingSuccess,
  submitFeedbackAndRatingFailure,

  // cancel consultation
  cancelCancelConsultationRequest,
  cancelConsultationRequest,
  cancelConsultationSuccess,
  cancelConsultationFailure,

  // follow up consultation
  followUpConsultationRequest,
  followUpConsultationSuccess,
  followUpConsultationFailure,

  // referral consultation
  referralConsultationRequest,
  referralConsultationSuccess,
  referralConsultationFailure,

  // manual category selection
  manualCategorySelectionRequest,
  manualCategorySelectionSuccess,
  manualCategorySelectionFailure,

  // specialist booking
  specialistBookingRequest,
  specialistBookingSuccess,
  specialistBookingFailure,

  // assign hospital specialist
  assignHospitalSpecialistRequest,
  assignHospitalSpecialistSuccess,
  assignHospitalSpecialistFailure,

  // get hospital details
  getHospitalDetailsRequest,
  getHospitalDetailsSuccess,
  getHospitalDetailsFailure,

  // get hospital specialists list
  getHospitalSpecialistsListRequest,
  getHospitalSpecialistsListSuccess,
  getHospitalSpecialistsListFailure,

  // select hospital specialist
  selectHospitalSpecialistRequest,
  selectHospitalSpecialistSuccess,
  selectHospitalSpecialistFailure,

  // select specialist and proceed with payment
  selectSpecialistAndProceedWithPaymentSuccess,
  selectSpecialistAndProceedWithPaymentFailure,
  selectSpecialistAndProceedWithPaymentRequest,

  // cancel and reset reducers
  cancelPatientDashboardRequest,
  resetPatientDashboardMessage,

  // other utlity reducers
  setCurrentConsultationTab,
  moveToStep4,
  resetPatientDashboardState,
  setFiles,
  resetSpecialistList,
  clearE10080Data,
  resetPaymentUrl,
  setPatientDashboardMessage,
  resetPaymentStatus,
  switchSpecialistFromPatientDashboardStep1,
  removeRejectedFiles,
  updateConsultationData,
  updateProgressBarStep,
  moveToNextStepOnProgressBarComplete,
  updateUploadAdditionalFilesProgressBarStep,
  setExistingFileIds,
  updateSpecialistData,

  // get specialist details
  getSpecialistDetailsRequest,
  getSpecialistDetailsSuccess,
  getSpecialistDetailsFailure,

  // get specialist feedbacks
  getSpecialistFeedbacksRequest,
  getSpecialistFeedbacksSuccess,
  getSpecialistFeedbacksFailure,

  // add consultation query
  addConsultationQueryRequest,
  addConsultationQuerySuccess,
  addConsultationQueryFailure,

  // hospital booking modal persistence
  setHospitalBookingModalState,
  updateHospitalBookingModalState,
  clearHospitalBookingModalState,
  activateOrDeactivateSpecialist,
  activateOrDeactivateHospitalSpecialist,

  // favourite doctors
  updateFavouriteStatus,

  removeFavouriteDoctorRequest,
  removeFavouriteDoctorSuccess,
  removeFavouriteDoctorFailure,

  addFavouriteDoctorRequest,
  addFavouriteDoctorSuccess,
  addFavouriteDoctorFailure,

  // recent cases
  getRecentCasesRequest,
  getRecentCasesSuccess,
  getRecentCasesFailure,
} = patientDashboardSlice.actions;
export default patientDashboardSlice.reducer;
